import { motion } from "framer-motion";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

type CaseStudy = {
  title: string;
  subtitle: string;
  metric: string;
  image: string;
};

type CaseStudiesSectionProps = {
  caseStudies: CaseStudy[];
};

export function CaseStudiesSection({ caseStudies }: CaseStudiesSectionProps) {
  return (
    <section id="case-studies" className="bg-slate-950 py-24">
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-semibold text-white sm:text-4xl">
              Featured case studies
            </h2>
            <p className="mt-4 text-slate-300">
              Proof that AVUX translates mission-critical requirements into measurable outcomes.
            </p>
          </div>
          <a
            href="#case-studies"
            className="text-sm font-medium text-emerald-300 hover:text-emerald-200"
          >
            View full library
          </a>
        </div>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-12 grid gap-6 md:grid-cols-2"
        >
          {caseStudies.slice(0, 3).map((study) => (
            <motion.article
              key={study.title}
              variants={itemVariants}
              className="group overflow-hidden rounded-3xl border border-white/10 bg-slate-900/40"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={study.image}
                  alt={`${study.title} preview`}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
              </div>
              <div className="space-y-3 px-8 py-8">
                <div className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-300">
                  {study.subtitle}
                </div>
                <h3 className="text-2xl font-semibold text-white">{study.title}</h3>
                <p className="text-sm text-slate-300">{study.metric}</p>
                <a
                  href="#case-studies"
                  className="inline-flex items-center text-sm font-medium text-emerald-300 hover:text-emerald-200"
                >
                  Explore the case →
                </a>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
